import { GetWorklistQueryVariables, WorklistItemStatus } from 'generated/graphql';
import type {
  ProcessedFilterStateType,
  ProcessedNumberRangeFilterValueType,
  ProcessedTextFilterValueType,
} from '../../WorklistFilters/types';
import { WorklistSurface, type WorklistSurfaceType } from '../types';

import { useWorklistFiltersContext } from '../../WorklistFilters/WorklistFiltersContext';
import { ageRangeToDOBRange } from '../../WorklistFilters/utils';

import { useMemo } from 'react';
import { ARCHIVE, IN_PROGRESS, PENDING, QC } from 'config/constants';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
export const WORKLIST_QUERY_LIMIT = 50;

const STATUS_MAP = {
  [WorklistSurface.MY_QUEUE]: IN_PROGRESS,
  [WorklistSurface.IN_PROGRESS]: IN_PROGRESS,
  [WorklistSurface.PENDING]: PENDING,
  [WorklistSurface.RECENTLY_READ]: ARCHIVE,
  [WorklistSurface.ARCHIVE]: ARCHIVE,
  [WorklistSurface.WORKLIST_VIEW]: null,
  [WorklistSurface.STUDIES_DIALOG]: null,
  [WorklistSurface.QC]: QC,
  [WorklistSurface.PATIENT_JACKET_PRIORS]: null,
  [WorklistSurface.PLACEHOLDER]: null,
  [WorklistSurface.GLOBAL_SEARCH]: null,
} as const;

export const useWorklistVariables = (
  userId: string | null | undefined,
  search: string,
  surface: WorklistSurfaceType,
  processedFilters: ProcessedFilterStateType
): GetWorklistQueryVariables => {
  const { sortColumns, sortOrders } = useWorklistFiltersContext();
  const [isQcStatusColumnEnabled] = useFeatureFlagEnabled(FF.QC_STATUS_COLUMN);

  const selectedStatus = processedFilters.status as ProcessedTextFilterValueType;

  const isActiveVariable =
    surface !== WorklistSurface.IN_PROGRESS || selectedStatus == null || selectedStatus.length !== 1
      ? undefined
      : selectedStatus[0] === 'Locked';

  const processedAgeData = useMemo(
    () =>
      processedFilters.age != null
        ? ageRangeToDOBRange(processedFilters.age as ProcessedNumberRangeFilterValueType)
        : null,
    [processedFilters.age]
  );

  const qcStatus = useMemo(
    () =>
      surface === WorklistSurface.QC
        ? isQcStatusColumnEnabled
          ? processedFilters.qcStatus
          : [WorklistItemStatus.Qc]
        : undefined,
    [isQcStatusColumnEnabled, processedFilters.qcStatus, surface]
  );

  return useMemo(
    () =>
      ({
        limit: WORKLIST_QUERY_LIMIT,
        claimedBy: surface === WorklistSurface.MY_QUEUE ? userId : null,
        sortOrder: sortOrders,
        sortColumn: sortColumns,
        status:
          surface === WorklistSurface.CUSTOM_WORKLIST
            ? processedFilters.status
            : STATUS_MAP[surface],
        filter: search,
        patientSex: processedFilters.patientSex,
        patientDob: processedFilters.patientDob ?? processedAgeData ?? undefined,
        studyDate: processedFilters.studyDate,
        submittedAt: processedFilters.submittedAt,
        modality: processedFilters.modality,
        priority: surface === WorklistSurface.PENDING ? ['-priority'] : [],
        activeViews: processedFilters.views,
        hasDraftReport: processedFilters.hasDraftReport,
        customStatus: processedFilters.customStatus,
        accessionNumberPartial: processedFilters.accessionNumber,
        mrnPartial: processedFilters.mrn,
        isActive: isActiveVariable,
        patientType: processedFilters.patientType,
        studyDescription: processedFilters.studyDescription,
        site: processedFilters.site,
        bodyPart: processedFilters.bodyPart,
        patientName: processedFilters.patientName,
        priorityFilter: processedFilters.priority,
        referringPhysicianPartial: processedFilters.referringPhysician,
        claimedByPartial: processedFilters.radiologist,
        signedBy: processedFilters.signedBy,
        qcStatus,
      }) as GetWorklistQueryVariables,
    [
      surface,
      userId,
      sortOrders,
      sortColumns,
      processedFilters.status,
      processedFilters.patientSex,
      processedFilters.patientDob,
      processedFilters.studyDate,
      processedFilters.submittedAt,
      processedFilters.modality,
      processedFilters.views,
      processedFilters.hasDraftReport,
      processedFilters.customStatus,
      processedFilters.accessionNumber,
      processedFilters.mrn,
      processedFilters.patientType,
      processedFilters.studyDescription,
      processedFilters.site,
      processedFilters.bodyPart,
      processedFilters.patientName,
      processedFilters.priority,
      processedFilters.referringPhysician,
      processedFilters.radiologist,
      processedFilters.signedBy,
      search,
      processedAgeData,
      isActiveVariable,
      qcStatus,
    ]
  );
};
